import {
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  ElementRef,
  Inject,
  ViewChild,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { isUndefined } from 'lodash';
import { Observable, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { setCssProperty } from '../../../../../cross-team-shared/misc-utils';
import { isPointCloudOutput } from '../../../../../cross-team-shared/misc-utils/output-utils';
import { Dimensions } from '../../../../../cross-team-shared/types/misc';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { ContentConnector } from '../../../../../team-apis/content/content-connector';
import { getOutputByName } from '../../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { contentConnectorToken } from '../../utils/misc-utils';
import { FusionManager } from '../fusion/fusion-manager';
import { InteractionModeService } from '../utils/interaction-mode.service';
import { OutputKeyboardShortcutService } from '../utils/output-keyboard-shortcut.service';
import { ZoomPanelService } from '../zoom-panel/zoom-panel.service';
import { AnnotationLayoutService } from './annotation-layout.service';

@Component({
  selector: 'h3-annotation-layout',
  templateUrl: './annotation-layout.component.html',
  styleUrls: ['./annotation-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AnnotationLayoutComponent
  extends InjectableSuperclass
  implements DoCheck
{
  status$ = this.store('status').$;
  errorMessage$ = this.store('errorMessage').$;
  interactionModeIsCanvas$ = this.interactionModeService.isInMode$('canvas');
  interactionModeIsNone$ = this.interactionModeService.isInMode$('none');
  interactionModeIsScrubber$ =
    this.interactionModeService.isInMode$('scrubber');
  annotationHeight$ = this.annotationLayoutService.getAnnotationHeight$();
  annotationWidth$ = this.annotationLayoutService.getAnnotationWidth$();
  isTimelineHidden$ = this.annotationLayoutService.isTimelineHidden$;
  isRightPanelHidden$ = this.annotationLayoutService.isRightPanelHidden$;
  isThumbnail$: Observable<boolean>;
  // public so we can manually trigger it in tests
  observer!: MutationObserver;
  observerCallback: (mutationList: MutationRecord[]) => void;
  isSelectedOutput$: Observable<boolean>;
  isInSplitView$ = this.taskWorkspaceStore('splitViewOutputName').$.pipe(
    map(isDefined),
  );

  protected isObjectsEnabled = this.taskWorkspaceStore.isUsingObjects();

  private focusableContainerElement!: HTMLElement;
  private mainDisplayTestElement?: ElementRef;
  private resizeObserver?: ResizeObserver;
  private outputName = this.outputInfoStore.state().output.name;

  constructor(
    private fusionManager: FusionManager,
    private interactionModeService: InteractionModeService,
    private keyboardShortcutService: OutputKeyboardShortcutService,
    private store: AbstractAnnotationStore,
    private zoomPanelService: ZoomPanelService,
    @Inject(outputInfoStoreToken) private outputInfoStore: Store<OutputInfo>,
    @Inject(contentConnectorToken) private contentConnector: ContentConnector,
    private annotationLayoutService: AnnotationLayoutService,
    private taskWorkspaceStore: TaskWorkspaceStore,
  ) {
    super();
    this.setUpDisplayTimelineSubscription();
    this.isThumbnail$ = this.taskWorkspaceStore.isThumbnail$(this.outputName);
    this.observerCallback = this.buildObserverCallback();
    this.isSelectedOutput$ = this.taskWorkspaceStore(
      'selectedOutputName',
    ).$.pipe(
      map((selectedOutputName) => selectedOutputName === this.outputName),
    );
  }

  @ViewChild('focusableContainer')
  set focusableContainer(ref: ElementRef | undefined) {
    if (!ref) {
      return;
    }

    this.focusableContainerElement = ref.nativeElement;
    this.observeDomMutations();
    this.observeAnnotationLayoutChanges(ref);
  }

  @ViewChild('mainDisplay') set mainDisplay(ref: ElementRef | undefined) {
    if (!ref || !ref.nativeElement) {
      return;
    }
    this.updateMainDisplaySize(ref.nativeElement.getBoundingClientRect());

    if (environment.shouldUseResizeObserver) {
      this.setUpSubscriptionToMainDisplay(ref);
    } else {
      this.mainDisplayTestElement = ref;
    }
  }

  ngDoCheck() {
    // HACK: ResizeObservers are not supported with zone.js async scheduling
    // Triggering the observer manually in tests is needed in multiple
    // locations. Where to add this trigger is not clear. This is the
    // best place I could find.
    if (!environment.shouldUseResizeObserver) {
      const mainDisplaySize =
        this.mainDisplayTestElement?.nativeElement?.getBoundingClientRect();
      if (isUndefined(mainDisplaySize)) {
        return;
      }
      this.updateMainDisplaySize(mainDisplaySize);
    }
  }

  onKeyboardEvent(event: KeyboardEvent) {
    if (this.store.state().status !== 'ready') {
      return;
    }

    this.keyboardShortcutService.handleKeyboardEvent(event);
  }

  onFocusOut(event: FocusEvent) {
    const focusIsFromChildElement = this.focusableContainerElement.contains(
      event.relatedTarget as Node,
    );
    if (this.zoomPanelService.isZoomPanelOpen() && !focusIsFromChildElement) {
      this.zoomPanelService.closeZoomPanel();
    }

    // not ideal, zoom service should be able to delay opening the zoom panel
    // itself, but it requires a too big refactor
    this.contentConnector.onAnnotationFocusChange(false);
  }

  onFocusIn() {
    // not ideal, zoom service should be able to delay opening the zoom panel
    // itself, but it requires a too big refactor
    this.contentConnector.onAnnotationFocusChange(true);
    // To support multiple independent outputs case,
    // we need to always set the focused output to be selected output
    this.taskWorkspaceStore('selectedOutputName').set(this.outputName);

    if (
      this.taskWorkspaceStore.isUsingObjects() &&
      this.taskWorkspaceStore.state().settings.zoomPanel.isOpen &&
      !isPointCloudOutput(
        getOutputByName(
          this.taskWorkspaceStore.state().project,
          this.outputName,
        ),
      )
    ) {
      this.zoomPanelService.openZoomPanel(true);
    }
  }

  private setUpDisplayTimelineSubscription() {
    this.subscribeTo(this.fusionManager.areAllSingleFrame$, (singleFrame) =>
      singleFrame
        ? this.annotationLayoutService.hideTimeline()
        : this.annotationLayoutService.displayTimeline(),
    );
  }

  private setUpSubscriptionToMainDisplay(ref: ElementRef) {
    const rxjsResizeObserver$ = this.buildRxjsResizeObserver(ref);
    this.subscribeTo(rxjsResizeObserver$, (sizeInfo) => {
      if (isUndefined(sizeInfo) || sizeInfo.length === 0) {
        return;
      }
      this.updateMainDisplaySize(sizeInfo[0].contentRect);
    });
  }

  private updateMainDisplaySize(size: Dimensions) {
    this.store('mainDisplaySize').set({
      width: size.width,
      height: size.height,
    });
  }

  private buildRxjsResizeObserver(ref: ElementRef) {
    return new Observable((subscriber: Subscriber<ResizeObserverEntry[]>) => {
      this.resizeObserver = new ResizeObserver((entries) => {
        subscriber.next(entries);
      });

      const element = ref.nativeElement;
      this.resizeObserver.observe(element);
      return () => {
        this.resizeObserver?.unobserve(element);
      };
    });
  }

  private buildObserverCallback() {
    return (mutationList: MutationRecord[]) => {
      mutationList.forEach((mutation) => {
        const tagName = (mutation.target as HTMLElement).tagName;
        if (
          tagName.toLocaleLowerCase().startsWith('h3-secondary-toolbar') &&
          mutation.removedNodes.length
        ) {
          // Ensures that the focus is kept on the output when a focused
          // secondary toolbar disappears, instead of focusing <body>
          this.focusableContainerElement.focus({ preventScroll: true });
        }
      });
    };
  }

  private observeDomMutations() {
    this.observer = new MutationObserver(this.observerCallback);
    this.observer.observe(this.focusableContainerElement, {
      childList: true,
      subtree: true,
    });
  }

  private observeAnnotationLayoutChanges(ref: ElementRef) {
    this.subscribeTo(
      this.annotationLayoutService.getRightPanelWidth$(),
      (width) => {
        setCssProperty(ref, '--ursa-shape-list-width', `${width}px`);
      },
    );
    this.subscribeTo(
      this.annotationLayoutService.getTimelineHeight$(),
      (height) => {
        setCssProperty(ref, '--ursa-timeline-height', `${height}px`);
      },
    );
  }
}
